import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import Page from '../../../../core/components/Page';
import SearchField from '../../../../core/components/SearchField.tsx';
import DateRangePresetFilter from '../../../../core/components/DateRangePresetFilter.tsx';
import { IFilter, SEARCH_FILTER_KEY } from '../../../../core/utils/filterQuery.ts';
import { isoDateFormat } from '../../../../core/utils/dates.ts';
import { useDebounce } from '../../../../core/utils/hooks/useDebounce';
import { withIcon } from '../../../../core/components/Icon';
import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import AgentStatusFilter from './AgentStatusFilter';
import AgentCreatedByFilter from './AgentCreatedByFilter';
import AgentSenderFilter from './AgentSenderFilter';

const FilterIcon = withIcon(faBarsFilter);

interface AgentFilterBarProps {
    filters: IFilter[];
    onFiltersChange: (filters: IFilter[]) => void;
    creators: any[];
    senders: string[];
    tabs?: React.ReactNode;
    actions?: React.ReactNode;
}

const datePresetOptions = (t: (key: string) => string) => {
    const today = new Date();
    return [
        {
            label: t('historyPage.filters.createdAt.options.today'),
            value: [format(today, isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('historyPage.filters.createdAt.options.lastWeek'),
            value: [format(subWeeks(today, 1), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('historyPage.filters.createdAt.options.lastMonth'),
            value: [format(subMonths(today, 1), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('historyPage.filters.createdAt.options.last3Months'),
            value: [format(subDays(today, 90), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
        {
            label: t('historyPage.filters.createdAt.options.lastYear'),
            value: [format(subYears(today, 1), isoDateFormat), format(today, isoDateFormat)].join(','),
        },
    ];
};

const AgentFilterBar = ({ filters, onFiltersChange, creators, senders, tabs, actions }: AgentFilterBarProps) => {
    const { t } = useTranslation('agents');
    const [filterBarVisible, setFilterBarVisible] = useState(false);

    let searchTerm = filters.find((f) => f.name === SEARCH_FILTER_KEY)?.value || '';
    if (Array.isArray(searchTerm)) searchTerm = searchTerm[0];
    const [searchInputValue, setSearchInputValue] = useState(searchTerm);

    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const propagateSearchFilter = useCallback(
        (value: string) => {
            const newFilters = filtersRef.current.filter((f) => f.name !== SEARCH_FILTER_KEY);
            if (value) {
                newFilters.push({ name: SEARCH_FILTER_KEY, value, exclude: false, expression: 'eq' });
            }
            onFiltersChange(newFilters);
        },
        [onFiltersChange]
    );

    const debouncedPropagateSearchFilter = useDebounce(propagateSearchFilter, 500);

    const handleSearchTermChange = (value: string) => {
        setSearchInputValue(value);
        debouncedPropagateSearchFilter(value);
    };

    useEffect(() => {
        setSearchInputValue(searchTerm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const nonSearchFilterCount = new Set(filters.filter((f) => f.name !== SEARCH_FILTER_KEY).map((f) => f.name)).size;

    return (
        <>
            <Page.Header className="py-0" primary={false}>
                <div className="flex flex-1 h-full">{tabs}</div>

                <div className="py-2.5 flex gap-2">
                    <SearchField
                        placeholder={t('historyPage.filters.search.placeholder')}
                        value={searchInputValue}
                        onValueChange={handleSearchTermChange}
                    />
                    <Page.HeaderButton
                        active={filterBarVisible}
                        onClick={() => setFilterBarVisible(!filterBarVisible)}
                        variant="ghost"
                    >
                        <FilterIcon /> {t('historyPage.filters.toggleLabel')}{' '}
                        {nonSearchFilterCount > 0 && `(${nonSearchFilterCount})`}
                    </Page.HeaderButton>

                    {actions}
                </div>
            </Page.Header>

            {filterBarVisible && (
                <div className="px-6 py-4 flex flex-wrap gap-2 bg-surface-medium border-b border-solid border-line-weak">
                    <AgentSenderFilter filters={filters} onFiltersChange={onFiltersChange} senders={senders} />
                    <AgentStatusFilter filters={filters} onFiltersChange={onFiltersChange} />
                    <AgentCreatedByFilter filters={filters} onFiltersChange={onFiltersChange} creators={creators} />
                    <DateRangePresetFilter
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                        filterName="created_at"
                        label={t('historyPage.filters.createdAt.label')}
                        presetOptions={datePresetOptions(t)}
                        maxDate={new Date()}
                    />
                    {filters.length > 0 && (
                        <button
                            className="text-content-strong px-3 text-sm font-medium hover:text-content-strong hover:bg-surface-strong rounded transition-colors"
                            onClick={() => onFiltersChange([])}
                        >
                            {t('historyPage.filters.clearAll')}
                        </button>
                    )}
                </div>
            )}
        </>
    );
};

export default AgentFilterBar;
